import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Template {
  id: string;
  name: string;
  specialty?: string;
  description?: string;
  chief_complaint_template?: string;
  anamnesis_template?: any;
  physical_exam_template?: any;
  diagnosis_template?: any;
  treatment_plan_template?: any;
  observations_template?: string;
}

interface TemplateSelectorProps {
  onSelectTemplate: (template: Template) => void;
  disabled?: boolean;
}

export function TemplateSelector({ onSelectTemplate, disabled }: TemplateSelectorProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  const { data: templates, isLoading } = useQuery({
    queryKey: ["clinical-record-templates"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Buscar templates do profissional e templates públicos
      const { data, error } = await supabase
        .from("clinical_record_templates" as unknown as "profiles")
        .select("*")
        .or(`professional_id.eq.${user.id},is_public.eq.true`)
        .order("is_default" as any, { ascending: false })
        .order("name" as any, { ascending: true });

      if (error) throw error;
      return (data as unknown as Template[]) || [];
    },
  });

  const handleApplyTemplate = () => {
    if (!selectedTemplateId) {
      toast.error("Selecione um template");
      return;
    }

    const template = templates?.find((t) => t.id === selectedTemplateId);
    if (template) {
      onSelectTemplate(template);
      toast.success(`Template "${template.name}" aplicado!`);
      setSelectedTemplateId("");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando templates...
      </div>
    );
  }

  if (!templates || templates.length === 0) {
    return null;
  }

  return (
    <div className="flex gap-2 items-end">
      <div className="flex-1">
        <Select
          value={selectedTemplateId}
          onValueChange={setSelectedTemplateId}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecionar template..." />
          </SelectTrigger>
          <SelectContent>
            {templates.map((template) => (
              <SelectItem key={template.id} value={template.id}>
                <div className="flex flex-col">
                  <span>{template.name}</span>
                  {template.specialty && (
                    <span className="text-xs text-muted-foreground">
                      {template.specialty}
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleApplyTemplate}
        disabled={!selectedTemplateId || disabled}
      >
        <FileText className="h-4 w-4 mr-2" />
        Aplicar
      </Button>
    </div>
  );
}

